import { isToday } from 'date-fns';
import { ensureUrlProtocol } from '@clarix/utils';
import { CHIP } from './constants';
import { CHIP_COPY } from './copy';
import {
  eventLabel,
  eventPlace,
  formatClock,
  formatDayClock,
  formatElapsed,
  formatShortDay,
  formatSpan,
  isLive,
  isPrivateMeeting,
  meetingPeople,
  minutesUntil,
  type EventPlace,
} from './format';
import type { ChipPerson, NextMeetingAction, NextMeetingDTO, PendingAction, WireEvent } from './types';

const MIN = 60_000;

function parseUrl(url: string): URL | null {
  try {
    return new URL(url);
  } catch {
    return null;
  }
}

/** A join link the chip may render as an href: scheme-less links get https, any other scheme (zoommtg:, javascript:) is dropped. */
function joinHref(url: string | null | undefined): string | null {
  if (!url) return null;
  // A link that already names its scheme keeps it; prefixing https would turn zoommtg://… into a broken web link.
  const parsed = parseUrl(url) ?? parseUrl(ensureUrlProtocol(url));
  return parsed && (parsed.protocol === 'https:' || parsed.protocol === 'http:') ? parsed.href : null;
}

/** What the chip is saying, in rising order of urgency. */
export type ChipKind = 'none' | 'action' | 'next' | 'soon' | 'imminent' | 'starting' | 'live';

export interface ChipView {
  kind: ChipKind;
  focus: WireEvent | null;
  /** The meeting's title, or null while titles are hidden in the top bar. */
  title: string | null;
  /** A private or masked focus: the pill shows a lock and says nothing else about it. */
  locked: boolean;
  /** The lead text: "in 12 min", "Starting now", "Live · 12:04", "MoM due", or Next's "6:30 PM" / "Fri 10:00 AM". */
  when: string;
  /** Next's "in 2 h 10 min", today only. */
  relative: string | null;
  /** The waiting meeting's short day ("Tue"), on the Waiting-on-you chip. */
  day: string | null;
  pendingAction: PendingAction | null;
  /** Meetings waiting on the caller (MoM, hours): the server's whole count. */
  count: number;
  actions: NextMeetingAction[];
  /** Set from five minutes before the start (contract §8 #19, as the drawer does) until it ends. */
  joinUrl: string | null;
  /** "6:25 PM" while a meeting with a link is not yet joinable. */
  joinOpens: string | null;
  /** The focus meeting's room or location (card, tooltip, label); null while titles are hidden. */
  place: EventPlace | null;
  /** The booked room's name in the pill itself, only once the meeting is close or live. */
  room: string | null;
  /** Empty while titles are hidden, for a private meeting, and on a calendar-api that sends none. */
  attendees: ChipPerson[];
}

/** `actions` is §18.6-additive; an older calendar-api only sends `pending`. */
function actionRows(data: NextMeetingDTO): NextMeetingAction[] {
  if (data.actions) return data.actions.slice(0, CHIP.LIST_LIMIT);
  return data.pending ? [{ action: data.pending.action, event: data.pending.event, recording_id: null }] : [];
}

/** The server decides which meetings matter; this only picks the words and the urgency. */
export function chipView(data: NextMeetingDTO, now: number, h24: boolean, hideTitles: boolean): ChipView {
  const focus = data.live ?? data.next;
  const actions = actionRows(data);
  const count = data.pending?.count ?? actions.length;
  const base = {
    focus,
    actions,
    count,
    locked: false,
    relative: null,
    day: null,
    pendingAction: null,
    joinUrl: null,
    joinOpens: null,
    place: null,
    room: null,
    attendees: [],
  };

  if (!focus) {
    const first = data.pending ? data.pending : actions[0];
    if (!first) return { ...base, kind: 'none', title: null, when: '' };
    return {
      ...base,
      kind: 'action',
      title: hideTitles ? null : eventLabel(first.event),
      when: CHIP_COPY.action[first.action],
      day: formatShortDay(first.event.start, now),
      pendingAction: first.action,
    };
  }

  const locked = isPrivateMeeting(focus);
  const title = hideTitles ? null : eventLabel(focus);
  const place = hideTitles ? null : eventPlace(focus);
  const room = place?.kind === 'room' ? place.name : null;
  const attendees = hideTitles || locked ? [] : meetingPeople(focus);
  const link = joinHref(focus.conference.url);
  const shown = { ...base, title, locked, place, room, attendees };

  if (isLive(focus)) {
    return {
      ...shown,
      kind: 'live',
      when: CHIP_COPY.liveFor(formatElapsed(now - Date.parse(focus.start))),
      joinUrl: link,
    };
  }
  const mins = minutesUntil(focus, now);
  const joinable = mins <= CHIP.IMMINENT_MIN;
  const opensAt = new Date(Date.parse(focus.start) - CHIP.IMMINENT_MIN * MIN).toISOString();
  const joinOpens = link && !joinable ? formatDayClock(opensAt, h24) : null;
  if (mins * MIN <= CHIP.SOON_MS) {
    const left = Math.max(0, mins);
    return {
      ...shown,
      kind: left === 0 ? 'starting' : joinable ? 'imminent' : 'soon',
      when: left === 0 ? CHIP_COPY.startingNow : CHIP_COPY.in(formatSpan(left)),
      joinUrl: joinable ? link : null,
      joinOpens,
    };
  }
  const today = isToday(new Date(focus.start));
  return {
    ...shown,
    kind: 'next',
    room: null,
    when: today ? formatClock(focus.start, h24) : formatDayClock(focus.start, h24),
    relative: today ? CHIP_COPY.in(formatSpan(mins)) : null,
    joinOpens,
  };
}
